import React from 'react';
import WithDescriptionTooltip from '@components/WithDescriptionTooltip/WithDescriptionTooltip';
import { FormatterType, HeadCellType, SortType, TimeBucketName, TimeBucketToDateGap } from '@fabric-ui/components/Table';
import { DateFormats } from '@fabric-ui/utils';
import {
  getDateBucketFromExpression,
  getFieldTitleByExpression,
  getFieldTypeByExpression,
  isDateTruncExpression,
} from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { format } from 'date-fns';
import { useGetMetric } from '../hooks/useMetricsContext';
import { hasTimeBucket } from '../views/CustomDashboards/EditCustomDashboard/components/TimeBucketBreakdown/utils';
import { useGetDisplayName } from '../views/CustomDashboards/hooks/useGetDisplayName';
import { useGetFieldType } from '../views/CustomDashboards/hooks/useGetFieldType';
import { DwQueryRequest, SortDir } from '../views/CustomDashboards/types';

export const useGetHeadCellsWithRequestObject = (request: DwQueryRequest): HeadCellType[] => {
  const getDisplayName = useGetDisplayName(request.projectionId.name);
  const getHeadCellType = useGetFieldType(request.projectionId.name);
  const { getMetric } = useGetMetric();
  const sort = request.sorting;
  const ids = [...request.select.dims, ...request.select.metrics];
  return ids.map(({ name, alias, expression }) => {
    const sortDir = sort.find(({ name: sortName }) => sortName === alias || sortName === name)?.dir;
    const finalName = alias || name;

    const commonSort = sortDir === SortDir.ASC ? SortType.ASC : sortDir === SortDir.DESC ? SortType.DESC : undefined;

    if (hasTimeBucket(name)) {
      return {
        id: name,
        label: TimeBucketName[name],
        type: FormatterType.historicDate,
        sort: commonSort,
        fieldName: name,
        formatter: date => (date ? format(new Date(date), DateFormats[TimeBucketToDateGap[name]]) : date),
      };
    }

    if (expression) {
      return {
        id: name,
        label: getFieldTitleByExpression({ expression, displayName: getDisplayName(name) }),
        type: getFieldTypeByExpression(expression),
        fieldName: isDateTruncExpression(expression) ? getDateBucketFromExpression(expression) : undefined,
      };
    }

    return {
      id: finalName,
      label: (
        <WithDescriptionTooltip title={getMetric(finalName)?.description} placement="top">
          <span>{getDisplayName(finalName)}</span>
        </WithDescriptionTooltip>
      ),
      type: getHeadCellType({ entityFieldKey: finalName }) as FormatterType,
      sort: commonSort,
    };
  });
};
